import { isObject, isSet, isString } from '@/global/utils/utils'

// Default append adornment of a datetime picker field: the app's own calendar
// SVG, rendered as an image (TextField pipes an `append` config through
// ImageField) rather than an mdi font icon. Single place to change it.
//
// Any ImageField prop is allowed here (src, width, height, contain, class, ...);
// `src` may also be a function returning a src, which ImageField awaits.
export const DEFAULT_DATETIME_APPEND = Object.freeze({
  src: '/img/icons/calendar-icon.svg',
  width: 20,
  height: 20,
  contain: true
})

/**
 * Decide what goes into a datetime picker field's append slot: an image, a mdi
 * icon, or nothing. Precedence:
 *   appendImage: { src, ... }  -> that image (any ImageField prop)
 *   appendImage: false         -> nothing at all
 *   appendIcon: ''             -> nothing at all
 *   appendIcon: 'mdi-...'      -> that font icon
 *   none of the above          -> `defaultImage` (the calendar image)
 *
 * TextField renders `append.src` through ImageField and `appendIcon` through
 * Vuetify, and both emit `append-click`, so the caller's open-the-picker wiring
 * is the same either way. The two are mutually exclusive: whichever one wins is
 * returned and the other comes back empty, since Vuetify would otherwise draw
 * the font icon into the same slot the image occupies.
 *
 * @param {Object} options `{ appendIcon, appendImage }` - unset (`null`/`undefined`) means "no preference"
 * @param {Object|false} defaultImage default for when nothing is specified
 * @returns {{ appendIcon: string, append: Object|undefined }} TextField config fragment
 */
export function resolveDatetimeAppend (options = {}, defaultImage = DEFAULT_DATETIME_APPEND) {
  const { appendIcon, appendImage } = options || {}

  const none = { appendIcon: '', append: undefined }

  if (appendImage === false || appendIcon === '') {
    return none
  }

  if (isSet(appendImage) && isObject(appendImage) && isString(appendImage.src)) {
    return { appendIcon: '', append: { ...appendImage } }
  }

  if (isString(appendIcon)) {
    return { appendIcon, append: undefined }
  }

  if (!isSet(defaultImage) || defaultImage === false) {
    return none
  }

  return { appendIcon: '', append: { ...defaultImage } }
}
